import { createFileRoute } from '@tanstack/react-router';
import { ListPlus } from 'lucide-react';
import { useMemo } from 'react';
import { atLeast } from '@regent/shared';
import { Button, EmptyState, SkeletonRows } from '@/components/ui';
import { useHierarchyIndex } from '@/features/hierarchy/api';
import { useHierarchyDialog } from '@/features/hierarchy/dialogs';
import { LocationIcon } from '@/features/hierarchy/location-icon';
import { LocationOverview } from '@/features/hierarchy/location-overview';
import { Page } from '@/features/shell/page';
import type { TaskScope } from '@/features/tasks/api';
import { TaskViewPage } from '@/features/tasks/task-view-page';

export const Route = createFileRoute('/w/$workspaceId/f/$folderId')({ component: FolderPage });

function FolderPage() {
  const { folderId } = Route.useParams();
  const index = useHierarchyIndex();
  const openDialog = useHierarchyDialog((s) => s.open);
  const folder = index.folder(folderId);
  const scope = useMemo<TaskScope>(() => ({ type: 'folder', id: folderId }), [folderId]);

  if (index.isLoading) {
    return (
      <Page title="Folder">
        <SkeletonRows className="p-6" />
      </Page>
    );
  }
  if (!folder) {
    return (
      <Page title="Folder">
        <EmptyState
          className="h-full"
          title="Folder not found"
          description="It may have been deleted, or you do not have access."
        />
      </Page>
    );
  }
  const title = `${index.space(folder.spaceId)?.name ?? ''} / ${folder.name}`;
  const newList = atLeast(folder.access, 'edit') ? (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => openDialog({ mode: 'create', kind: 'list', spaceId: folder.spaceId, folderId })}
    >
      <ListPlus /> New list
    </Button>
  ) : undefined;

  if (!index.lists.some((l) => l.folderId === folderId)) {
    return (
      <Page title={title} icon={<LocationIcon kind="folder" icon={folder.icon} />} actions={newList}>
        <LocationOverview spaceId={folder.spaceId} folderId={folderId} />
      </Page>
    );
  }
  return (
    <TaskViewPage
      scope={scope}
      title={title}
      icon={<LocationIcon kind="folder" icon={folder.icon} />}
      actions={newList}
    />
  );
}
